import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Rocket, Check, Loader2, CheckCircle2, Circle, ArrowRight, Target, Calendar as CalIcon, Clock, TrendingUp, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Markdown } from "../components/Markdown";
import { Button } from "../components/ui/button";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { journeyT, getPriority } from "../lib/journey";
import { buildProfileContext } from "../lib/profile";
import { api, formatError } from "../lib/api";

export default function PlanStudio() {
  const nav = useNavigate();
  const { data, launchPlan, toggleAction } = useApp();
  const { lang } = useLang();
  const J = journeyT(lang);

  const planMissions = (data?.actions || []).filter((a) => a.isMission);
  const hasPlan = !!data?.plan && planMissions.length > 0;
  const priority = getPriority(data, lang);

  const [phase, setPhase] = useState("idle"); // idle | loading | ready | launching | done
  const [plan, setPlan] = useState(null); // { objective, missions[<=4] }
  const [commPhase, setCommPhase] = useState(null); // null | loading | ready
  const [comm, setComm] = useState(null); // { action, contentType, text, image }

  const durationWeeks = (missions) => {
    const max = Math.max(0, ...missions.map((m) => (typeof m.day_offset === "number" ? m.day_offset : 0)));
    return Math.max(1, Math.ceil((max + 1) / 7));
  };

  // Pick ONE plan action that needs a communication, dynamically (never hardcoded).
  const pickCommAction = () => {
    const ms = (data?.actions || []).filter((a) => a.isMission);
    if (!ms.length) return null;
    const socialRe = /instagram|facebook|réseau|red social|social|story|publication|post|reel|annonc/i;
    return (
      ms.find((m) => m.needs_content) ||
      ms.find((m) => socialRe.test(`${m.channel || ""} ${m.title || ""} ${m.type || ""}`)) ||
      ms.find((m) => ["publication", "promotion", "animation", "evenement"].includes(m.type)) ||
      ms[0]
    );
  };
  const channelToType = (ch) => {
    const k = (ch || "").toLowerCase();
    for (const key of ["instagram", "facebook", "story", "email", "sms", "whatsapp"]) if (k.includes(key)) return key;
    return "instagram";
  };

  // WOW finale: turn the selected plan action into a ready-to-use post (text + AI visual).
  const generateComm = async () => {
    const action = pickCommAction();
    if (!action) return;
    const contentType = channelToType(action.channel);
    const visualChannel = ["instagram", "facebook", "story"].includes(contentType) ? contentType : "instagram";
    setComm({ action, contentType, text: "", image: "" });
    setCommPhase("loading");
    const biz = data?.onboarding?.business_name || "mon commerce";
    const sector = data?.onboarding?.activity_type || "";
    const objective = data?.plan?.objective || action.title;
    const pctx = buildProfileContext(data);
    const [cRes, vRes] = await Promise.allSettled([
      api.post("/ai/content", { content_type: contentType, tone: "chaleureux", topic: action.title, business_name: biz, action: "generate", previous: "", profile_context: pctx }, { timeout: 120000 }),
      api.post("/ai/visual", { channel: visualChannel, objective, content: action.title, business_name: biz, sector, profile_context: pctx, reference_image: "" }, { timeout: 120000 }),
    ]);
    const text = cRes.status === "fulfilled" ? cRes.value.data.content : "";
    const image = vRes.status === "fulfilled" ? vRes.value.data.image : "";
    if (!text && !image) { toast.error(J.retry); setCommPhase(null); return; }
    setComm({ action, contentType, text, image });
    setCommPhase("ready");
  };

  const generate = async () => {
    setPhase("loading");
    try {
      const { data: p } = await api.post(
        "/ai/plan",
        { message: priority?.title || "Plan de croissance", context: priority?.why || "", profile_context: buildProfileContext(data) },
        { timeout: 120000 }
      );
      const missions = (p.missions || []).slice(0, 4); // keep it simple & readable
      if (!missions.length) throw new Error("empty");
      setPlan({ objective: p.objective, missions });
      setPhase("ready");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(J.retry, { description: detail ? formatError(detail) : J.preparingPlan });
      setPhase("idle");
    }
  };

  const launch = () => {
    if (!plan) return;
    setPhase("launching");
    const res = launchPlan({ objective: plan.objective, missions: plan.missions, key: priority?.title || plan.objective });
    if (res.ok || res.duplicate) setPhase("done");
    else { toast.error(J.retry); setPhase("ready"); }
  };

  // ---------- ACTIVE PLAN VIEW ("Continue my plan") ----------
  // Skipped while phase==="done" so the launch confirmation shows first.
  if (hasPlan && phase !== "done") {
    const total = planMissions.length;
    const doneCount = planMissions.filter((m) => m.done).length;
    const pct = total ? Math.round((doneCount / total) * 100) : 0;
    const next = [...planMissions.filter((m) => !m.done)].sort((a, b) => (a.day_offset ?? 99) - (b.day_offset ?? 99))[0];
    return (
      <Layout>
        <div className="max-w-xl mx-auto space-y-5" data-testid="plan-active">
          <div className="flex items-center gap-2 text-neon"><Rocket size={18} /><h1 className="font-display font-black text-xl">{J.planTitle}</h1></div>
          {data.plan?.objective && (
            <div className="lb-card p-4">
              <p className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{J.objective}</p>
              <p className="text-sm font-medium mt-1" data-testid="plan-active-objective">{data.plan.objective}</p>
            </div>
          )}
          <div className="lb-card p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">{J.progress}</span>
              <span className="text-neon font-display font-black" data-testid="plan-active-progress">{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-primary to-neon transition-all" style={{ width: `${pct}%` }} /></div>
            {next && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">{J.nextAction} : <span className="text-white/90">{next.title}</span></p>}
          </div>
          {/* WOW finale — turn the plan into a ready-to-use post */}
          <Button onClick={generateComm} data-testid="comm-cta" className="w-full rounded-full h-14 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-bold text-base">
            <Sparkles size={19} className="mr-2" /> ✨ {J.commCta}
          </Button>
          <div className="space-y-2" data-testid="plan-active-actions">
            {planMissions.map((m) => (
              <button key={m.id} onClick={() => toggleAction(m.id)} data-testid={`plan-action-${m.id}`}
                className="w-full lb-card p-3.5 flex items-center gap-3 text-left hover:border-neon/30 border border-white/5 transition-colors">
                {m.done ? <CheckCircle2 size={20} className="text-emerald-400 shrink-0" /> : <Circle size={20} className="text-[hsl(var(--muted-foreground))] shrink-0" />}
                <span className={`text-sm ${m.done ? "line-through text-[hsl(var(--muted-foreground))]" : ""}`}>{m.title}</span>
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => nav("/app/dashboard")} variant="outline" className="flex-1 rounded-full h-11 border-white/10 bg-white/5" data-testid="plan-back-dashboard">{J.seeMyPlan}</Button>
            <Button onClick={() => nav("/app/calendar")} variant="outline" className="rounded-full h-11 px-4 border-white/10 bg-white/5" data-testid="plan-open-calendar"><CalIcon size={16} /></Button>
          </div>
        </div>
        {commPhase && createPortal(
          <div className="fixed inset-0 bg-[#0a0a0f]/95 backdrop-blur-md overflow-y-auto" style={{ zIndex: 100 }} data-testid="comm-overlay">
            <div className="max-w-xl mx-auto min-h-screen flex flex-col px-4 py-6">
              {commPhase === "loading" ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="comm-loading">
                  <motion.div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-neon flex items-center justify-center mb-6" animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 1.2 }}>
                    <Sparkles size={30} className="text-white" />
                  </motion.div>
                  <p className="font-display font-bold text-lg">{J.commPreparing}</p>
                </div>
              ) : (
                <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col min-h-screen" data-testid="comm-ready">
                  <div className="flex items-center justify-between mb-4">
                    <p className="font-display font-black text-lg text-neon">✨ {J.commReady}</p>
                    <button onClick={() => setCommPhase(null)} data-testid="comm-close" className="text-[hsl(var(--muted-foreground))] hover:text-white"><X size={22} /></button>
                  </div>
                  {comm?.image && <img src={comm.image} alt="" className="w-full rounded-2xl border border-white/10 mb-4" data-testid="comm-visual" />}
                  <div className="lb-card p-4 mb-4">
                    <p className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">{J.commPostText}</p>
                    <div className="text-sm" data-testid="comm-text"><Markdown>{comm?.text || ""}</Markdown></div>
                  </div>

                  {/* Et ensuite ? — conclusion démo (aucune donnée réelle, aucun appel IA) */}
                  <div className="beam p-[1px] mb-4" data-testid="demo-whats-next">
                    <div className="rounded-[1.2rem] bg-[var(--lb-card)] p-5">
                      <div className="flex items-center gap-2 mb-1"><Sparkles size={17} className="text-neon" /><h3 className="font-display font-black text-lg">{J.whatsNextTitle}</h3></div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{J.whatsNextLead}</p>
                      <div className="space-y-1.5 mb-4">
                        {(J.whatsNextSteps || []).map((s, i) => (
                          <div key={i} className="flex items-center gap-3" data-testid={`whats-next-step-${i}`}>
                            <span className="shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-primary to-neon flex items-center justify-center text-[11px] font-bold text-white">{i + 1}</span>
                            <span className="text-sm font-medium">{s}</span>
                            {i < (J.whatsNextSteps.length - 1) && <ArrowRight size={13} className="text-neon/40 ml-auto" />}
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-white/90 font-medium border-t border-white/5 pt-3">{J.whatsNextTagline}</p>
                      <div className="mt-4 rounded-2xl border border-neon/25 bg-neon/[0.06] p-4 text-center" data-testid="demo-pricing">
                        <p className="font-display font-bold text-sm mb-1">{J.readyTitle}</p>
                        <p className="font-display font-black text-3xl neon-text leading-none my-1" data-testid="demo-pricing-amount">{J.readyPrice}</p>
                        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-3">{J.readyConditions}</p>
                        <Button onClick={() => nav("/register")} data-testid="demo-pricing-cta" className="w-full rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold text-sm">{J.readyCta} <ArrowRight size={15} className="ml-1.5" /></Button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-auto flex gap-2 sticky bottom-0 py-2 bg-[#0a0a0f]/60">
                    <Button variant="outline" onClick={generateComm} data-testid="comm-regenerate" className="flex-1 rounded-full h-12 border-white/10 bg-white/5"><RefreshCw size={16} className="mr-1.5" /> {J.regenerate}</Button>
                    <Button onClick={() => { toast.success(J.commUsed); setCommPhase(null); }} data-testid="comm-use" className="flex-1 rounded-full h-12 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold"><Check size={16} className="mr-1.5" /> {J.usePost}</Button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>,
          document.body
        )}
      </Layout>
    );
  }

  // ---------- GENERATION VIEW ----------
  return (
    <Layout>
      <div className="max-w-xl mx-auto" data-testid="plan-studio">
        <AnimatePresence mode="wait">
          {phase === "done" ? (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10" data-testid="plan-launched">
              <motion.div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-neon flex items-center justify-center mb-6" animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 0.5 }}>
                <Check size={40} className="text-white" />
              </motion.div>
              <h1 className="font-display font-black text-2xl mb-2">✅ {J.planLaunched}</h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-sm mx-auto mb-7">{J.actionsScheduled(plan?.missions?.length || 4)}</p>
              <Button onClick={() => nav("/app/dashboard")} className="rounded-full h-12 px-8 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold" data-testid="plan-see-cta">
                {J.seeMyPlan} <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </motion.div>
          ) : phase === "ready" && plan ? (
            <motion.div key="ready" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="plan-ready">
              <div className="flex items-center gap-2 text-neon"><Sparkles size={18} /><h1 className="font-display font-black text-xl">{J.priorityLabel}</h1></div>
              <div className="beam p-[1px]"><div className="rounded-[1.2rem] bg-[var(--lb-card)] p-5 space-y-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><Target size={13} /> {J.objective}</p>
                  <p className="text-base font-semibold mt-1 leading-snug" data-testid="plan-objective">{plan.objective}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-white/[0.03] border border-white/5 p-2.5 flex items-center gap-2">
                    <Clock size={15} className="text-neon" /><div><p className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">{J.duration}</p><p className="text-xs font-semibold">{J.durationWeeks(durationWeeks(plan.missions))}</p></div>
                  </div>
                  <div className="rounded-xl bg-white/[0.03] border border-white/5 p-2.5 flex items-center gap-2">
                    <TrendingUp size={15} className="text-neon" /><div><p className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">{J.expectedResult}</p><p className="text-xs font-semibold leading-tight">{priority?.impactShort || "—"}</p></div>
                  </div>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">{J.planActions} ({plan.missions.length})</p>
                  <div className="space-y-2" data-testid="plan-actions-list">
                    {plan.missions.map((m, i) => (
                      <div key={i} className="flex items-start gap-3 rounded-xl bg-white/[0.03] border border-white/5 p-3">
                        <span className="w-6 h-6 rounded-full bg-neon/15 text-neon flex items-center justify-center shrink-0 text-xs font-bold">{i + 1}</span>
                        <span className="text-sm leading-snug">{m.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div></div>
              <Button onClick={launch} disabled={phase === "launching"} className="w-full rounded-full h-14 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-bold text-base" data-testid="plan-launch-cta">
                {phase === "launching" ? <Loader2 className="animate-spin" size={18} /> : <>🚀 {J.launchCta}</>}
              </Button>
            </motion.div>
          ) : phase === "loading" ? (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16" data-testid="plan-loading">
              <Loader2 size={40} className="animate-spin text-neon mx-auto mb-5" />
              <p className="font-display font-bold text-lg">{J.preparingPlan}</p>
            </motion.div>
          ) : (
            <motion.div key="idle" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="py-6" data-testid="plan-idle">
              <div className="flex items-center gap-2 text-neon mb-3"><Target size={18} /><h1 className="font-display font-black text-xl">{J.priorityLabel}</h1></div>
              {priority && (
                <div className="lb-card p-5 mb-6">
                  <h2 className="font-display font-black text-xl leading-tight mb-2" data-testid="plan-priority-title">{priority.title}</h2>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{priority.why}</p>
                </div>
              )}
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{J.planIntro}</p>
              <Button onClick={generate} className="w-full rounded-full h-14 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-bold text-base" data-testid="plan-generate-cta">
                <Sparkles size={19} className="mr-2" /> ✨ {J.createPlanCta}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}
